import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

// Allowed API base URLs (whitelist)
const ALLOWED_API_URLS = [
    'https://testing.salesflow.shethink.in',
    'https://salesflow.shethink.in',
    'https://api.salesflow.shethink.in',
    'https://salesflow.homans.ai',
];

const baseURL = process.env.NEXT_PUBLIC_API_URL || 'https://salesflow.homans.ai';

// Validate base URL is in whitelist
if (!ALLOWED_API_URLS.some(url => baseURL.startsWith(url))) {
    console.error('Invalid API URL configured');
}

// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
    baseURL,
    timeout: 300000, // 30 seconds (fixed from 3000000)
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest', // Helps prevent CSRF
    },
    withCredentials: false, // Set to true if you need to send cookies
});// Request interceptor
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Add auth token if available
        const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Log request in development
        if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DEBUG_MODE === 'true') {
            // console.log('🚀 Request:', {
            //     method: config.method?.toUpperCase(),
            //     url: config.url,
            //     data: config.data,
            // });
        }

        return config;
    },
    (error: AxiosError) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        // Log response in development
        if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DEBUG_MODE === 'true') {
            // console.log('✅ Response:', {
            //     status: response.status,
            //     url: response.config.url,
            //     data: response.data,
            // });
        }
        return response;
    },
    (error: AxiosError) => {
        // Handle different error types
        if (error.response) {
            console.log(error.response)
            console.log(error.response.data)
            console.log(error.response.status)

            // Server responded with error status
            const status = error.response.status;
            const data = error.response.data as any;

            // Handle 401 Unauthorized - logout user
            if (status === 401) {
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    if (window.location.pathname !== '/login') {
                        window.location.href = '/login';
                    }
                }
            }
            console.error('❌ Response Error:', {
                status,
                url: error.config?.url,
                data,
            });
        } else if (error.request) {
            // Request made but no response received
            toast.error('Network error. Please check your internet connection.');
        } else {
            // Something else happened
            console.error('❌ Error:', error.message);
            toast.error(error.message || 'An unexpected error occurred.');
        }
        return Promise.reject(error);
    }
);

// Helper function to set auth token
export const setAuthToken = (token: string | null) => {
    if (token) {
        if (typeof window !== 'undefined') {
            localStorage.setItem('auth_token', token);
        }
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
        if (typeof window !== 'undefined') {
            localStorage.removeItem('auth_token');
        }
        delete axiosInstance.defaults.headers.common['Authorization'];
    }
};

// Helper function to clear auth token
export const clearAuthToken = () => {
    setAuthToken(null);
};

// Generic request wrapper with type safety
export const apiRequest = async <T = any>(
    config: AxiosRequestConfig
): Promise<T> => {
    try {
        const response = await axiosInstance.request<T>(config);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Convenient methods with error handling to prevent Next.js error overlay
export const api = {
    get: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response = await axiosInstance.get<T>(url, config);
            return response.data;
        } catch (error: any) {
            // Re-throw 401 errors to allow interceptor to handle logout
            if (error?.response?.status === 401) {
                throw error;
            }
            // Return empty/error response instead of throwing to prevent error overlay
            console.error('API GET Error:', url, error);
            return { success: false, error: 'Request failed' } as T;
        }
    },

    post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response = await axiosInstance.post<T>(url, data, config);
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 409) {
                throw error;
            }
            if (error?.response?.status === 401) {
                throw error = "Unauthorized";
            }
            console.error('API POST Error:', url, error);
            const errData = error?.response?.data;
            const errMsg = errData?.detail || errData?.message || 'Request failed';
            return { success: false, error: errMsg, detail: errData?.detail } as T;
        }
    },

    put: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response = await axiosInstance.put<T>(url, data, config);
            return response.data;
        } catch (error: any) {
            // Re-throw 401 errors to allow interceptor to handle logout
            if (error?.response?.status === 401) {
                throw error;
            }
            console.error('API PUT Error:', url, error);
            return { success: false, error: 'Request failed' } as T;
        }
    },

    patch: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response = await axiosInstance.patch<T>(url, data, config);
            return response.data;
        } catch (error: any) {
            // Re-throw 401 errors to allow interceptor to handle logout
            if (error?.response?.status === 401) {
                throw error;
            }
            console.error('API PATCH Error:', url, error);
            return { success: false, error: 'Request failed' } as T;
        }
    },

    delete: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
        try {
            const response = await axiosInstance.delete<T>(url, config);
            return response.data;
        } catch (error: any) {
            // Re-throw 401 errors to allow interceptor to handle logout
            if (error?.response?.status === 401) {
                throw error;
            }
            console.error('API DELETE Error:', url, error);
            return { success: false, error: 'Request failed' } as T;
        }
    },
};

export default axiosInstance;
